import { AppModule } from 'app/module';
import { shareApi } from 'util/detect';
import { share } from 'util/share';
import { mediaQueries } from 'util/mediaQueries';
import { Input } from 'module/input/main';
import { trackGa4 } from 'util/googleAnalytics';
import { directFocusTo } from 'util/a11y';
import './styles.scss';
import Template from './template.hbs';

const subject = 'Job-Empfehlung für dich';

export class ShareModal extends AppModule {
    setTemplate() {
        this.template = Template;
        if (module.hot) {
            module.hot.accept('./template.hbs', () => {
                this.template = Template;
                this.render();
            });
        }
    }

    domBindings() {
        return {
            overlay: '.m-shareModal__overlay',
            content: '.m-shareModal__content',
            shareLink: '.m-shareModal__link .m-input',
            copyButton: '.m-shareModal__copy',
            closeTrigger: '.m-shareModal__close',
            shareMailLink: '.m-shareModal__email',
            optionLinks: ['.m-shareModal__optionLink'],
            shareWhatsapp: '.m-shareModal__whatsApp',
            shareFacebook: '.m-shareModal__facebook',
            shareX: '.m-shareModal__x',
            shareXing: 'm-shareModal__xing',
            shareLinkedin: 'm-shareModal__linkedin',
        };
    }

    getPropsFromDom() {
        const shareLinks = {};
        for (let i = 0; i < this.dom.optionLinks.length; i += 1) {
            const type = this.dom.optionLinks[i].getAttribute('name');
            shareLinks[type] = this.dom.optionLinks[i].href;
        }

        return {
            open: false,
            isSaved: false,
            jobRecommend: {
                body: this.dom.shareMailLink.getAttribute('data-body'),
                bodyWithoutUrl: this.dom.shareMailLink.getAttribute('data-body-without-url'),
                shareLinks,
                jobId: this.dom.el.getAttribute('data-job-id') ?? '',
            },
        };
    }

    subs() {
        if (this.dom.shareLink) {
            this.shareLink = new Input(this.dom.shareLink);
        }
    }

    domEvents() {
        if (this.dom.el.classList.contains('m-shareModal--open') && mediaQueries.isDesktop()) {
            document.addEventListener('click', (e) => this.closeModal(e));
            window.addEventListener('blur', () => this.close());
        }
        if (this.dom.copyButton) {
            this.dom.copyButton.addEventListener('click', (e) => this.copy(e));
        }
        if (this.dom.overlay) {
            this.dom.overlay.addEventListener('click', () => this.closeViaClick());
        }
        if (this.dom.closeTrigger) {
            this.dom.closeTrigger.addEventListener('click', () => this.closeViaClick());
        }
        if (this.dom.optionLinks) {
            for (let i = 0; i < this.dom.optionLinks.length; i += 1) {
                this.dom.optionLinks[i].addEventListener('click', (e) => this.shareViaLink(e));
            }
        }
    }

    closeModal(e) {
        if (this.dom.content && this.dom.content.contains(e.target)) return;
        this.close();
    }

    share() {
        if (shareApi() && !mediaQueries.isDesktop()) {
            this.shareJob(
                decodeURIComponent(window.atob(this.props.jobRecommend.bodyWithoutUrl)),
                this.props.jobRecommend.shareLinks.karriere,
            );
        } else {
            this.updateProps({ open: !this.props.open }, true);

            if (!mediaQueries.isDesktop()) {
                this.openShareDialog();
            }
            if (mediaQueries.isMobile()) {
                this.events.emit('pushState');
            }
        }
    }

    shareJob(text, jobLink) {
        share(
            {
                subject,
                text,
                jobLink,
                utmParams: '?utm_source=karriere.at&utm_medium=web-share&utm_campaign=job-recommendation',
            },
            this.onShareSuccess,
        );
        trackGa4({
            event: 'share',
            method: 'Web Share',
            content_type: 'job',
            item_id: this.props.jobRecommend.jobId ?? null,
        });
    }

    shareViaLink(e) {
        e.preventDefault();
        if (e.target.classList.contains('m-shareModal__email')) {
            window.location.href = `mailto:?subject=${subject}&body=${window.atob(this.props.jobRecommend.body)}`;
            trackGa4({
                event: 'share',
                method: 'E-Mail',
                content_type: 'job',
                item_id: this.props.jobRecommend.jobId ?? null,
            });
        } else {
            window.open(e.target.href);

            let method = '';

            if (e.target.classList.contains('m-shareModal__whatsapp')) {
                method = 'WhatsApp';
            } else if (e.target.classList.contains('m-shareModal__facebook')) {
                method = 'Facebook';
            } else if (e.target.classList.contains('m-shareModal__xing')) {
                method = 'XING';
            } else if (e.target.classList.contains('m-shareModal__linkedin')) {
                method = 'LinkedIn';
            }

            trackGa4({
                event: 'share',
                method,
                content_type: 'job',
                item_id: this.props.jobRecommend.jobId ?? null,
            });
        }
    }

    copy(e) {
        e.stopPropagation();
        this.updateProps({ isSaved: true }, true);
        this.shareLink.setFocus();
        this.shareLink.select();
        document.execCommand('copy');
        trackGa4({
            event: 'share',
            method: 'Link',
            content_type: 'job',
            item_id: this.props.jobRecommend.jobId ?? null,
        });
    }

    open() {
        if (!this.props.open) {
            if (!mediaQueries.isDesktop()) {
                this.openShareDialog();
            }
            this.updateProps({ open: true }, true);
        }
    }

    closeViaClick() {
        if (this.props.open) {
            if (mediaQueries.isMobile()) {
                window.history.back();
            } else {
                if (!mediaQueries.isDesktop()) {
                    this.events.emit('removeShareModalStyles');
                    this.returnToOriginalContainer();
                }
                this.updateProps({ open: false }, true);
            }
        }
    }

    close() {
        if (this.props.open) {
            if (!mediaQueries.isDesktop()) {
                this.events.emit('removeShareModalStyles');
                this.returnToOriginalContainer();
            }
            this.updateProps({ open: false }, true);
        }
    }

    openShareDialog() {
        this.events.emit('addShareModalStyles');
        this.originalContainer = this.dom.el.parentElement;

        // Poor man's portal solution so the fixed layout can escape the css container it's trapped in
        document.body.appendChild(this.dom.el);
        directFocusTo(this.dom.copyButton);
    }

    returnToOriginalContainer() {
        this.originalContainer.appendChild(this.dom.el);
    }

    isOpen() {
        return this.props.open;
    }
}
